import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import {
  ConnectionHandler,
  Environment,
  commitLocalUpdate,
  useLazyLoadQuery,
  useMutation,
  usePaginationFragment,
  useRelayEnvironment,
} from "react-relay";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { RecordProxy } from "relay-runtime";
import { NotificationsTabArchiveMutation } from "./__generated__/NotificationsTabArchiveMutation.graphql";
import { NotificationsTabDeleteMutation } from "./__generated__/NotificationsTabDeleteMutation.graphql";
import { NotificationsTabPaginationQuery } from "./__generated__/NotificationsTabPaginationQuery.graphql";
import { NotificationsTabPendingPaginationQuery } from "./__generated__/NotificationsTabPendingPaginationQuery.graphql";
import { NotificationsTabQuery } from "./__generated__/NotificationsTabQuery.graphql";
import { NotificationsTab_brand$key } from "./__generated__/NotificationsTab_brand.graphql";
import { NotificationsTab_brandPendingNotifications$key } from "./__generated__/NotificationsTab_brandPendingNotifications.graphql";
import nullthrows from "../../../utils/nullthrows";
import NotificationForm from "./notifications/NotificationForm";
import NotificationsTable from "./notifications/NotificationsTable";

export enum DialogType {
  ARCHIVE = "archive",
  DELETE = "delete",
  NONE = "none",
}

const archiveNotifMutation = graphql`
  mutation NotificationsTabArchiveMutation($input: NodeInput!) {
    archiveNotification(input: $input) {
      ... on GraphQLNotification {
        isArchived
      }
    }
  }
`;

const deleteNotifMutation = graphql`
  mutation NotificationsTabDeleteMutation($input: NodeInput!) {
    deleteNotification(input: $input) {
      ... on GraphQLNotification {
        id
      }
    }
  }
`;

const query = graphql`
  query NotificationsTabQuery($id: ID!) {
    brand(id: $id) {
      ...NotificationsTab_brand
      ...NotificationsTab_brandPendingNotifications
    }
  }
`;

const brandConfirmedNotificationsFragment = graphql`
  fragment NotificationsTab_brand on BrandType
  @refetchable(queryName: "NotificationsTabPaginationQuery")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String", defaultValue: "" }
  ) {
    id
    notifications(
      first: $count
      after: $cursor
      filters: { confirmationStatus: { exact: "confirmed" } }
    ) @connection(key: "NotificationsTab_notifications", filters: []) {
      edges {
        node {
          id
          ...NotificationTableRow_notification
        }
      }
      totalCount
    }
  }
`;

const brandPendingNotificationsFragment = graphql`
  fragment NotificationsTab_brandPendingNotifications on BrandType
  @refetchable(queryName: "NotificationsTabPendingPaginationQuery")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String", defaultValue: "" }
  ) {
    id
    pendingNotifications: notifications(
      first: $count
      after: $cursor
      filters: { confirmationStatus: { exact: "pending" } }
    ) @connection(key: "NotificationsTab_pendingNotifications", filters: []) {
      edges {
        node {
          id
          ...NotificationTableRow_notification
        }
      }
      totalCount
    }
  }
`;

export const getNotificationTabConnections = (brand: RecordProxy<{}>) => {
  return [
    ConnectionHandler.getConnection(brand, "NotificationsTab_notifications"),
    ConnectionHandler.getConnection(
      brand,
      "NotificationsTab_pendingNotifications"
    ),
  ].filter(Boolean) as RecordProxy<{}>[];
};

function orderNotifsInRelayStore(environment: Environment, brandID: string) {
  return commitLocalUpdate(environment, (store) => {
    const brandRecord = store.get(brandID);
    if (brandRecord == null) {
      return;
    }

    const notificationRecord = brandRecord.getLinkedRecord("notifications");
    if (notificationRecord == null) {
      return;
    }

    const sortedEdges = notificationRecord
      .getLinkedRecords("edges")
      ?.sort((a, b) => {
        const aTime = dayjs(
          a.getLinkedRecord("node")?.getValue("timestamp") + ""
        ).unix();
        const bTime = dayjs(
          b.getLinkedRecord("node")?.getValue("timestamp") + ""
        ).unix();

        return bTime - aTime;
      });
    if (sortedEdges == null) {
      return;
    }
    notificationRecord.setLinkedRecords(sortedEdges, "edges");
  });
}

const NotificationsTab = ({ brandID }: { brandID: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const environment = useRelayEnvironment();

  const data = useLazyLoadQuery<NotificationsTabQuery>(query, {
    id: brandID,
  });
  const {
    data: brandWithConfirmedNotifications,
    loadNext,
    isLoadingNext,
    hasNext,
  } = usePaginationFragment<
    NotificationsTabPaginationQuery,
    NotificationsTab_brand$key
  >(brandConfirmedNotificationsFragment, data.brand);

  const {
    data: brandWithPendingNotifications,
    loadNext: loadNextPending,
    isLoadingNext: isLoadingNextPending,
    hasNext: hasNextPending,
  } = usePaginationFragment<
    NotificationsTabPendingPaginationQuery,
    NotificationsTab_brandPendingNotifications$key
  >(brandPendingNotificationsFragment, data.brand);

  const confirmedNotifications = brandWithConfirmedNotifications.notifications;
  const pendingNotifications =
    brandWithPendingNotifications.pendingNotifications;

  const [archiveNotification, isArchiving] =
    useMutation<NotificationsTabArchiveMutation>(archiveNotifMutation);
  const [deleteNotification, isDeleting] =
    useMutation<NotificationsTabDeleteMutation>(deleteNotifMutation);

  const [justUpdatedNotifID, setJustUpdatedNotifID] = useState<string | null>(
    null
  );
  const onUpdateComplete = (notifID: string | null) => {
    if (notifID) {
      setJustUpdatedNotifID(notifID);
      setTimeout(() => {
        setJustUpdatedNotifID(null);
      }, 2000);
    }

    orderNotifsInRelayStore(environment, brandID);
  };

  // Dialog State
  const [dialogNotifID, setDialogNotifID] = useState<string | null>(null);
  const [dialogType, setDialogType] = useState<DialogType>(DialogType.NONE);
  const isDialogOpen = dialogNotifID !== null;
  const onDialogClose = () => {
    setDialogNotifID(null);
    setDialogType(DialogType.NONE);
  };

  const onOpenDialog = (dialogType: DialogType, notificationId: string) => {
    setDialogType(dialogType);
    setDialogNotifID(notificationId);
  };

  const onArchive = useCallback(
    (id: string) => {
      archiveNotification({
        variables: {
          input: {
            id: id,
          },
        },
        onCompleted: (data, errors) => {
          if (errors) {
            console.log("Error: ", data, errors);
          } else {
            onDialogClose();
          }
        },
      });
    },
    [archiveNotification]
  );

  const onDelete = useCallback(
    (id: string) => {
      deleteNotification({
        variables: {
          input: {
            id: id,
          },
        },
        onCompleted: (data, errors) => {
          if (errors) {
            console.log("Error: ", data, errors);
          } else {
            onDialogClose();
          }
        },
        updater: (store) => {
          const payload = store.getRootField("deleteNotification");

          const brand = store.get(brandID);
          if (brand == null) {
            return;
          }

          const notifConnections = getNotificationTabConnections(brand);
          notifConnections.forEach((connection) => {
            const node = connection
              .getLinkedRecords("edges")
              ?.find(
                (edge) =>
                  edge.getLinkedRecord("node")?.getDataID() ===
                  payload.getDataID()
              );
            if (node == null) {
              return;
            }
            ConnectionHandler.deleteNode(connection, payload.getDataID());

            const notifCount = connection.getValue("totalCount");
            if (notifCount != null) {
              connection.setValue(Number(notifCount) - 1, "totalCount");
            }
          });
        },
      });
    },
    [brandID, deleteNotification]
  );

  // Dialog State
  let dialogTitle = "Are you sure you want to archive this notification?";
  let dialogText =
    "Your customers won’t be able to view your notification after you archive it.";
  let dialogButtonText = isArchiving ? "Archiving" : "Archive";
  let onDialogClick = () => onArchive(nullthrows(dialogNotifID));

  if (dialogType === DialogType.DELETE) {
    dialogTitle = "Are you sure you want to delete this notification?";
    dialogText =
      "Your customers won’t be able to view your notification after you delete it.";
    dialogButtonText = isDeleting ? "Deleting" : "Delete";
    onDialogClick = () => onDelete(nullthrows(dialogNotifID));
  }
  const dialogView = isDialogOpen ? (
    <Dialog open={isDialogOpen} onClose={onDialogClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{dialogText}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDialogClose} color="primary">
          Cancel
        </Button>
        <Button
          disabled={isArchiving || isDeleting}
          onClick={onDialogClick}
          color="error"
        >
          {dialogButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;

  const tableView = (
    <>
      <Stack spacing={2} direction="column" alignItems="flex-end">
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          sx={{
            display: "flex",
          }}
          onClick={() => {
            navigate(location.pathname + "/create");
          }}
        >
          Create Notification
        </Button>
      </Stack>
      <Stack spacing={4} pt={4}>
        {(pendingNotifications.totalCount ?? 0) > 0 && (
          <NotificationsTable
            notifications={pendingNotifications}
            title="AI-Suggested Notification"
            isLoadingNext={isLoadingNextPending}
            hasNext={hasNextPending}
            onLoadNext={loadNextPending}
            onOpenDialog={onOpenDialog}
            justUpdatedNotifID={justUpdatedNotifID}
            hidePastPublished
          />
        )}
        <NotificationsTable
          notifications={confirmedNotifications}
          title="Notification"
          isLoadingNext={isLoadingNext}
          hasNext={hasNext}
          onLoadNext={loadNext}
          onOpenDialog={onOpenDialog}
          justUpdatedNotifID={justUpdatedNotifID}
        />
      </Stack>
      {dialogView}
    </>
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Container maxWidth="lg">{tableView}</Container>}
      />
      <Route
        path="/create"
        element={
          <Container maxWidth="md">
            <NotificationForm
              brandID={brandID}
              onUpdateComplete={onUpdateComplete}
            />
          </Container>
        }
      />
      <Route
        path="/edit/:id"
        element={
          <Container maxWidth="md">
            <NotificationForm
              brandID={brandID}
              onUpdateComplete={onUpdateComplete}
            />
          </Container>
        }
      />
    </Routes>
  );
};

export default NotificationsTab;
